// frontend/src/components/dashboard/pos/dialogs/CalculatorDialog.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close,
  Backspace,
  Payment
} from '@mui/icons-material';
import { gradients } from '../styles/posStyles';

const CalculatorDialog = ({
  open,
  onClose,
  amountReceived,
  setAmountReceived,
  handleQuickAmount,
  handleCashWithChangePayment,
  cartItems,
  totalAmount
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  
  // ✅ Responsive breakpoints detection
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Handle number input
  const handleNumberInput = (value) => {
    if (amountReceived === '0') {
      setAmountReceived(value);
    } else {
      setAmountReceived(amountReceived + value);
    }
  };

  // Handle backspace
  const handleBackspace = () => {
    if (amountReceived.length > 1) {
      setAmountReceived(amountReceived.slice(0, -1));
    } else {
      setAmountReceived('0');
    }
  };
  
  // Handle decimal point
  const handleDecimalPoint = () => {
    if (!amountReceived.includes('.')) {
      setAmountReceived(amountReceived + '.');
    }
  };
  
  // Handle clear
  const handleClear = () => {
    setAmountReceived('0');
  };

  // Handle dialog close with proper cleanup
  const handleClose = (event, reason) => {
    // Prevent closing on backdrop click if needed
    if (reason === 'backdropClick') {
      return;
    }
    
    // Clear any focused elements
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
    onClose(event, reason);
  };

  // Numeric keypad buttons
  const numpadButtons = [
    '7', '8', '9',
    '4', '5', '6',
    '1', '2', '3',
    '0', '.', 'C'
  ];

  return (
    <Dialog 
      open={open}
      onClose={handleClose}
      // ✅ FIXED: Better responsive sizing
      maxWidth={isMobile ? 'xs' : isTablet ? 'xs' : 'sm'}
      fullWidth
      fullScreen={isMobile} // Full screen on mobile
      disableRestoreFocus={true}
      disableAutoFocus={false}
      disableEnforceFocus={false}
      keepMounted={false}
      PaperProps={{
        sx: { 
          borderRadius: isMobile ? 0 : 2,
          overflow: 'hidden',
          // ✅ FIXED: Set fixed heights to prevent scrolling
          height: isMobile ? '100vh' : isTablet ? '85vh' : '90vh',
          maxHeight: isMobile ? '100vh' : isTablet ? '85vh' : '90vh',
          display: 'flex',
          flexDirection: 'column',
          '&:focus': {
            outline: 'none'
          }
        }
      }}
      aria-labelledby="calculator-dialog-title"
      aria-describedby="calculator-dialog-description"
    >
      {/* ✅ FIXED: Header with fixed height */}
      <DialogTitle 
        component="div"
        id="calculator-dialog-title"
        sx={{ 
          flexShrink: 0, // Don't shrink
          background: gradients.primary,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: isMobile ? 56 : isTablet ? 64 : 72, // Fixed heights
          px: isMobile ? 2 : 3,
          py: 0 // No vertical padding to maintain fixed height
        }}
      >
        <Typography variant={isMobile ? "subtitle1" : isTablet ? "h6" : "h6"} sx={{ fontWeight: 600 }}>
          {t('pos.dialog.cashPaymentTitle')}
        </Typography>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={handleClose}
          size={isMobile ? "small" : "medium"}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      
      {/* ✅ FIXED: Content with flex layout to fill remaining space */}
      <DialogContent sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        p: isMobile ? 1.5 : isTablet ? 2 : 2.5,
        overflow: 'hidden' // Prevent scrolling
      }}>
        {/* ✅ FIXED: Amount Display - Fixed size */}
        <Box sx={{ 
          flexShrink: 0, // Don't shrink
          mb: isMobile ? 1 : isTablet ? 1.5 : 2, 
          p: isMobile ? 1 : isTablet ? 1.5 : 2, 
          bgcolor: 'rgba(102, 126, 234, 0.08)',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: isMobile ? 80 : isTablet ? 100 : 120 // Fixed minimum height
        }}>
          <Typography 
            id="calculator-dialog-description"
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              fontSize: isMobile ? '0.7rem' : isTablet ? '0.75rem' : '0.875rem',
              mb: 0.5
            }}
          >
            {t('pos.dialog.amountReceived')}
          </Typography>
          <Typography 
            variant={isMobile ? "h5" : isTablet ? "h4" : "h3"} 
            sx={{ 
              fontWeight: 700, 
              color: '#667eea',
              fontSize: isMobile ? '1.5rem' : isTablet ? '1.8rem' : '2.5rem',
              lineHeight: 1
            }}
          >
            ${amountReceived}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mt: 0.5, 
            justifyContent: 'space-between',
            width: '100%'
          }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: isMobile ? '0.7rem' : isTablet ? '0.75rem' : '0.875rem' }}
            >
              {t('pos.dialog.orderTotal')}:
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 600,
                fontSize: isMobile ? '0.8rem' : isTablet ? '0.9rem' : '1rem'
              }}
            >
              ${totalAmount.toFixed(2)}
            </Typography>
          </Box>
          {parseFloat(amountReceived) >= totalAmount && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mt: 0.5, 
              justifyContent: 'space-between',
              width: '100%',
              color: 'success.main'
            }}>
              <Typography 
                variant="body2"
                sx={{ fontSize: isMobile ? '0.7rem' : isTablet ? '0.75rem' : '0.875rem' }}
              >
                {t('pos.dialog.change')}:
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 600,
                  fontSize: isMobile ? '0.8rem' : isTablet ? '0.9rem' : '1rem'
                }}
              >
                ${(parseFloat(amountReceived) - totalAmount).toFixed(2)}
              </Typography>
            </Box>
          )}
        </Box>
        
        {/* ✅ FIXED: Scrollable content area */}
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0 // Allow shrinking
        }}>
          <Divider sx={{ mb: isMobile ? 1 : isTablet ? 1.5 : 2 }} />
          
          {/* Quick Amount Buttons - Compact */}
          <Typography 
            variant="subtitle2" 
            sx={{ 
              mb: 0.5, 
              fontWeight: 500, 
              color: '#667eea',
              fontSize: isMobile ? '0.7rem' : isTablet ? '0.75rem' : '0.875rem'
            }}
          >
            {t('pos.dialog.quickAmounts')}:
          </Typography>
          <Grid container spacing={isMobile ? 0.5 : 1} sx={{ mb: isMobile ? 1 : 1.5 }}>
            {[5, 10, 20, 50, 100].map((amount) => (
              <Grid item xs={amount === 100 ? 12 : 6} sm={amount === 100 ? 12 : 3} key={amount}>
                <Button 
                  variant="outlined"
                  onClick={() => handleQuickAmount(amount)}
                  sx={{
                    borderRadius: isMobile ? '4px' : '6px',
                    borderColor: '#667eea',
                    color: '#667eea',
                    '&:hover': {
                      backgroundColor: 'rgba(102, 126, 234, 0.1)',
                      borderColor: '#764ba2',
                    },
                    width: '100%',
                    height: isMobile ? 28 : isTablet ? 32 : 36, // Smaller heights
                    fontSize: isMobile ? '0.65rem' : isTablet ? '0.7rem' : '0.75rem',
                    minHeight: 'unset'
                  }}
                >
                  ${amount}
                </Button>
              </Grid>
            ))}
          </Grid>
          
          {/* ✅ FIXED: Compact Numeric Keypad */}
          <Typography 
            variant="subtitle2" 
            sx={{ 
              mb: 0.5, 
              fontWeight: 500, 
              color: '#667eea',
              fontSize: isMobile ? '0.7rem' : isTablet ? '0.75rem' : '0.875rem'
            }}
          >
            {t('pos.dialog.enterAmount')}:
          </Typography>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Grid container spacing={isMobile ? 0.5 : 1} sx={{ 
              flex: 1,
              '& .MuiGrid-item': {
                display: 'flex'
              }
            }}>
              {numpadButtons.map((btn) => (
                <Grid item xs={4} key={btn}>
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{
                      height: isMobile ? 35 : isTablet ? 40 : 45, // Smaller heights
                      fontSize: isMobile ? '1rem' : isTablet ? '1.1rem' : '1.2rem',
                      borderRadius: isMobile ? '4px' : '6px',
                      borderColor: btn === 'C' ? '#f44336' : '#e0e0e0',
                      color: btn === 'C' ? '#f44336' : '#333',
                      minHeight: 'unset',
                      '&:hover': {
                        backgroundColor: btn === 'C' ? 'rgba(244, 67, 54, 0.04)' : 'rgba(0, 0, 0, 0.04)',
                        borderColor: btn === 'C' ? '#f44336' : '#bdbdbd',
                      }
                    }}
                    onClick={() => {
                      if (btn === 'C') {
                        handleClear();
                      } else if (btn === '.') {
                        handleDecimalPoint();
                      } else {
                        handleNumberInput(btn);
                      }
                    }}
                  >
                    {btn}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
          
          {/* ✅ FIXED: Compact Backspace Button */}
          <Button
            variant="outlined"
            fullWidth
            onClick={handleBackspace}
            startIcon={<Backspace />}
            sx={{
              mt: isMobile ? 1 : 1.5,
              mb: isMobile ? 1 : 1.5,
              height: isMobile ? 32 : isTablet ? 36 : 40, // Smaller height
              borderRadius: isMobile ? '4px' : '6px',
              borderColor: '#e0e0e0',
              color: '#333',
              fontSize: isMobile ? '0.7rem' : isTablet ? '0.75rem' : '0.875rem',
              flexShrink: 0
            }}
          >
            {t('pos.dialog.backspace')}
          </Button>
        </Box>
        
        {/* ✅ FIXED: Action Buttons - Fixed at bottom */}
        <Grid container spacing={isMobile ? 1 : 2} sx={{ flexShrink: 0 }}>
          <Grid item xs={6}>
            <Button
              variant="outlined"
              fullWidth
              size={isMobile ? "small" : "medium"}
              onClick={handleClose}
              sx={{ 
                height: isMobile ? 36 : isTablet ? 40 : 48,
                fontSize: isMobile ? '0.7rem' : isTablet ? '0.75rem' : '0.875rem'
              }}
            >
              {t('common.cancel')}
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="contained"
              fullWidth
              size={isMobile ? "small" : "medium"}
              onClick={handleCashWithChangePayment}
              disabled={cartItems.length === 0 || parseFloat(amountReceived) < totalAmount}
              startIcon={<Payment />}
              sx={{
                height: isMobile ? 36 : isTablet ? 40 : 48,
                background: gradients.primary,
                fontSize: isMobile ? '0.7rem' : isTablet ? '0.75rem' : '0.875rem',
                '&:hover': {
                  background: gradients.primaryHover,
                },
                '&:disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                }
              }}
            >
              {t('pos.dialog.processPayment')}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default CalculatorDialog;